import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lock, LogIn, Mail } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  type FormErrors,
} from '@/lib/form-errors';
import { ROUTES } from '@/routes/paths';
import { AuthCard } from './AuthCard';
import { FormErrorAlert } from './FormErrorAlert';
import { resolveAuthRedirect } from './auth-redirect';
import { validateEmail, validateLoginPassword } from './auth-validation';
import { useLogin } from './use-login';

/**
 * Paso 2 del plan (`docs/FRONTEND_PLAN.md` §4): inicio de sesión.
 *
 * Endpoint: `POST /api/auth/login` (público) → `{ token, user, tenants }`.
 *
 * Decisiones:
 * - `<form>` de verdad (enter funciona) y con `noValidate`: los mensajes de validación son los
 *   nuestros, en español y debajo del campo, no los del navegador.
 * - Los errores se pintan en dos sitios: por campo (validación del cliente o detalle de Zod si
 *   se puede atribuir a un campo) y en un `Alert` con el mensaje del backend ("Credenciales
 *   inválidas" en un 401) o con los mensajes que Zod agrupa bajo `body`.
 * - La sesión la aplica el hook (`applyAuthResult`, ver `use-login.ts`); aquí solo se navega a
 *   la ruta de origen (`state.from`) o al Panel.
 * - El 401 de este endpoint también avisa al handler global de sesión (el cliente HTTP lo
 *   llama con cualquier 401), pero aquí no hay sesión que limpiar y la caché se vuelve a
 *   sembrar al entrar: no tiene efecto visible.
 */

/** Campos del formulario: sirven para asociar los mensajes de Zod (ver `src/lib/form-errors.ts`). */
const FIELDS = ['email', 'password'] as const;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const loginMutation = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** Al escribir se limpia el error de ese campo y el aviso general (ya no aplican). */
  function clearError(field: string) {
    setSubmitError(null);
    setErrors((current) => {
      if (!current.fieldErrors[field] && current.formMessages.length === 0) return current;

      const fieldErrors = { ...current.fieldErrors };
      delete fieldErrors[field];
      return { formMessages: [], fieldErrors };
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const emailError = validateEmail(email);
    const passwordError = validateLoginPassword(password);

    if (emailError || passwordError) {
      setErrors({
        formMessages: [],
        fieldErrors: {
          ...(emailError ? { email: [emailError] } : {}),
          ...(passwordError ? { password: [passwordError] } : {}),
        },
      });
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      await loginMutation.mutateAsync({ email: email.trim(), password });
      navigate(resolveAuthRedirect(location.state), { replace: true });
    } catch (error) {
      setErrors(formErrorsFromApiError(error, FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  const emailError = fieldError(errors, 'email');
  const passwordError = fieldError(errors, 'password');

  return (
    <AuthCard
      title="Iniciar sesión"
      description="Entra para ver las conversaciones, pedidos y campañas de tu negocio."
      footer={
        <>
          ¿Todavía no tienes cuenta?{' '}
          <Link to={ROUTES.register} className="font-medium text-brand-700 hover:underline">
            Crear una cuenta
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorAlert message={submitError} errors={errors} />

        <Field label="Email" htmlFor="email" required error={emailError}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="tu@negocio.com"
            leftIcon={<Mail size={16} />}
            invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'email-error' : undefined}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              clearError('email');
            }}
          />
        </Field>

        <Field label="Contraseña" htmlFor="password" required error={passwordError}>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            placeholder="Tu contraseña"
            leftIcon={<Lock size={16} />}
            invalid={Boolean(passwordError)}
            aria-describedby={passwordError ? 'password-error' : undefined}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              clearError('password');
            }}
          />
        </Field>

        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={loginMutation.isPending}
          leftIcon={<LogIn size={16} />}
        >
          Entrar
        </Button>
      </form>
    </AuthCard>
  );
}
